import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ProjectDetail } from '../../../contracts/projects.types';
import { projectTitleClusterMessages as m } from '../../../data/projects/components/projectTitleCluster.messages';
import { useUpdateProject } from '../hooks';

export interface ProjectTitleClusterProps {
  /** Active project, or null/undefined while metadata is still loading. */
  project: ProjectDetail | null | undefined;
  /** Active project id — required with ``project`` to enable rename. */
  projectId: string | null | undefined;
  /** Shown instead of the title while ``project`` is unresolved and no ``leading``. */
  fallback?: ReactNode;
  /** Optional control rendered left of the title while not editing. */
  leading?: ReactNode;
  /**
   * When ``none``, skip the static title text (e.g. a project selector in
   * ``leading`` already shows the name). Rename input still replaces the
   * whole cluster while editing.
   */
  titleDisplay?: 'text' | 'none';
  /** Controlled rename mode — driven by the host's overflow menu. */
  isEditing: boolean;
  onEditingChange: (editing: boolean) => void;
  className?: string;
  /** Extra classes on the rename input. */
  inputClassName?: string;
}

/**
 * What the app supplies: the rename mutation, which also refreshes the app's
 * chat lists. Module-level, so its identity is stable.
 */
export interface ProjectTitleClusterHost {
  useUpdateProject: (projectId: string) => { mutate: (body: { title: string }) => void };
}

/**
 * Shared title | rename-input cluster for Work project surfaces. Owns the
 * draft + rename mutation; edit mode is controlled by the host so the
 * right-side overflow menu can enter rename without an imperative handle.
 *
 * Deliberately not a full header — shell chrome (sidebar toggle, fullscreen,
 * close, more menu) stays with each host.
 */
export const ProjectTitleClusterView = ({
  project,
  projectId,
  fallback,
  leading,
  titleDisplay = 'text',
  isEditing,
  onEditingChange,
  className,
  inputClassName,
  useUpdateProject,
}: ProjectTitleClusterProps & ProjectTitleClusterHost) => {
  const intl = useIntl();
  const updateProject = useUpdateProject(projectId ?? '');

  const [titleDraft, setTitleDraft] = useState(project?.title ?? '');
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;
  const inputRef = useRef<HTMLInputElement>(null);

  // Reseed the draft when the active project (or its committed title)
  // changes and drop edit mode, so a swap never inherits an in-progress rename.
  useEffect(() => {
    setTitleDraft(project?.title ?? '');
    onEditingChangeRef.current(false);
  }, [projectId, project?.title]);

  const commitTitle = useCallback(() => {
    onEditingChange(false);
    if (!project) return;
    const next = titleDraft.trim();
    if (!next || next === project.title) {
      setTitleDraft(project.title);
      return;
    }
    updateProject.mutate({ title: next });
  }, [project, titleDraft, updateProject, onEditingChange]);

  // Rename is entered from the overflow menu, so take focus once the menu has
  // finished closing (it prevents close-autofocus rather than returning focus
  // to its trigger). Owning focus is what makes ``onBlur`` the single dismiss
  // path — an outside click blurs the input and commits.
  useEffect(() => {
    if (!isEditing) return;

    const focusId = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(focusId);
  }, [isEditing]);

  const renameInputLabel = intl.formatMessage(m.renameInputLabel);

  if (!project || !projectId) {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        {leading ?? fallback ?? (
          <TypographyP2 className="text-text-secondary">
            <FormattedMessage {...m.loading} />
          </TypographyP2>
        )}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={cn('flex min-w-0 items-center gap-2', className)}>
        <input
          ref={inputRef}
          autoFocus
          aria-label={renameInputLabel}
          className={cn(
            'min-w-0 rounded-md border border-border bg-surface px-2 py-1 text-sm font-medium text-text-primary outline-none ring-0 focus:border-border-pressed',
            inputClassName
          )}
          value={titleDraft}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              onEditingChange(false);
              setTitleDraft(project.title);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn('flex min-w-0 items-center gap-2', className)}>
      {leading ? <div className="min-w-0 flex-1">{leading}</div> : null}
      {titleDisplay === 'text' ? (
        <span className="truncate text-sm font-normal text-text-primary">
          {project.title}
        </span>
      ) : null}
    </div>
  );
};

/** The Work title cluster, bound to the rename mutation. */
export const ProjectTitleCluster = (props: ProjectTitleClusterProps) => (
  <ProjectTitleClusterView {...props} useUpdateProject={useUpdateProject} />
);
